import * as Popover from '@radix-ui/react-popover';
import React, {
  useRef,
  useState,
  type MouseEvent,
  type PointerEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { AttachmentChip } from '@/components/AttachmentChip';
import { AttachmentPreview } from '@/components/AttachmentPreview';
import { AutoHideScrollArea } from '@/components/AutoHideScrollArea';
import { ChevronDown } from '@/components/Icons/ChevronDown';
import { Paperclip } from '@/components/Icons/Paperclip';
import { useIsMobile } from '@/hooks/useIsMobile';
import type { Attachment } from '@/types/attachment';
import { AttachmentsDisplay } from '@/types/components';
import { tv } from '@/utils/tv';

const MAX_INLINE_ATTACHMENTS = 2;
const HOVER_CLOSE_DELAY_MS = 150;

const trigger = tv({
  base: 'flex w-fit max-w-full min-w-0 cursor-pointer items-center gap-2 rounded-[40px] px-2 py-1 text-body-13 text-primary transition-colors',
  variants: {
    isOpen: {
      true: 'bg-hover',
      false: 'bg-on-primary-surface hover:bg-hover',
    },
  },
});

const isMouse = (event: PointerEvent) => event.pointerType === 'mouse';

const isMouseClick = (event: MouseEvent) =>
  (event.nativeEvent as globalThis.PointerEvent).pointerType === 'mouse';

interface Props {
  attachments: Attachment[];
  display?: AttachmentsDisplay;
}

export const DiaryAttachments: React.FC<Props> = ({
  attachments,
  display = AttachmentsDisplay.Aggregated,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState<Attachment>();

  const isAggregated =
    display === AttachmentsDisplay.Aggregated &&
    !isMobile &&
    attachments.length > MAX_INLINE_ATTACHMENTS;
  const closeTimeoutRef = useRef<number>(undefined);

  const openOnHover = (event: PointerEvent) => {
    if (!isMouse(event)) {
      return;
    }

    window.clearTimeout(closeTimeoutRef.current);
    setIsOpen(true);
  };

  const closeOnHoverEnd = (event: PointerEvent) => {
    if (!isMouse(event)) {
      return;
    }

    closeTimeoutRef.current = window.setTimeout(
      () => setIsOpen(false),
      HOVER_CLOSE_DELAY_MS,
    );
  };

  const keepHoverOpenOnClick = (event: MouseEvent) => {
    if (isOpen && isMouseClick(event)) {
      event.preventDefault();
    }
  };

  const openPreview = (attachment: Attachment) => {
    setIsOpen(false);
    setPreviewAttachment(attachment);
  };

  if (!attachments.length || display === AttachmentsDisplay.Hidden) {
    return null;
  }

  if (!isAggregated) {
    return (
      <div className="flex min-w-0 flex-col items-start gap-[6px]">
        {attachments.map((attachment) => (
          <AttachmentChip key={attachment.id} attachment={attachment} />
        ))}
      </div>
    );
  }

  return (
    <>
      <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
        <Popover.Trigger
          onPointerEnter={openOnHover}
          onPointerLeave={closeOnHoverEnd}
          onClick={keepHoverOpenOnClick}
          className={trigger({ isOpen })}
        >
          <Paperclip className="size-4 shrink-0" />
          <span className="truncate font-medium">
            {t('diary.attachments', { count: attachments.length })}
          </span>
          <ChevronDown
            className={`size-4 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          />
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            side="bottom"
            align="start"
            sideOffset={8}
            collisionPadding={16}
            onOpenAutoFocus={(event) => event.preventDefault()}
            onPointerEnter={openOnHover}
            onPointerLeave={closeOnHoverEnd}
            className="z-50 w-[318px] max-w-[calc(100vw-32px)] overflow-hidden rounded-[14px] bg-card shadow-card"
          >
            <AutoHideScrollArea viewportClassName="max-h-[246px]">
              <div className="flex flex-col gap-2 p-2">
                {attachments.map((attachment) => (
                  <AttachmentChip
                    key={attachment.id}
                    attachment={attachment}
                    isFullWidth
                    onPreview={openPreview}
                  />
                ))}
              </div>
            </AutoHideScrollArea>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {previewAttachment && (
        <AttachmentPreview
          attachment={previewAttachment}
          onClose={() => setPreviewAttachment(undefined)}
          onDownloaded={() => setPreviewAttachment(undefined)}
        />
      )}
    </>
  );
};
